import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Layout } from "../../components/layout";
import { AllSessionsPageStyled } from "./AllSessionsPage.styled";
import { sessionsService, Session } from "../../services/sessionsService";
import { patientsService, Patient } from "../../services/patientsService";
import { Provider } from "../../types/api";
import { selectUser } from "../../store/selectors";
import { ASSISTANT_ROLES } from "../../constants";
import SessionsHeader from "./SessionsHeader";
import SessionsTable from "./SessionsTable";
import { usersService } from "../../services/usersService";

type SortField = string;
type SortDirection = "asc" | "desc";

interface SessionDisplay extends Record<string, unknown> {
  id: number;
  provider: {
    id?: number;
    name?: string;
    avatarUrl?: string;
  };
  patient: string;
  patientId: number;
  patientUid: string;
  type: string;
  date: string;
  treatmentPlan?: {
    id: number;
    name: string;
  };
}

const AllSessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector(selectUser);

  // Get initial patient filter from URL
  const initialPatientId = searchParams.get("patientId")
    ? Number(searchParams.get("patientId"))
    : null;

  // Check if user is Manager or Assistant
  const isManagerOrAssistantRole = ASSISTANT_ROLES.includes(
    user?.roleTitle as (typeof ASSISTANT_ROLES)[number],
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("date");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const sessionsPerPage = 25;

  // Debounce search input - 500ms delay to let user finish typing
  const prevSearchRef = useRef(searchQuery);
  useEffect(() => {
    // Skip if value is the same (prevents initial double-fetch)
    if (prevSearchRef.current === searchQuery) {
      return;
    }
    prevSearchRef.current = searchQuery;

    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  const [sessions, setSessions] = useState<SessionDisplay[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters state
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviders, setSelectedProviders] = useState<number[]>([]);
  const [debouncedProviders, setDebouncedProviders] = useState<number[]>([]);
  const [providersLoading, setProvidersLoading] = useState(false);
  const providersLoadedRef = useRef(false);

  const [patients, setPatients] = useState<Patient[]>([]);
  const [selectedPatients, setSelectedPatients] = useState<number[]>(
    initialPatientId ? [initialPatientId] : [],
  );
  const [debouncedPatients, setDebouncedPatients] = useState<number[]>(
    initialPatientId ? [initialPatientId] : [],
  );
  const [patientsLoading, setPatientsLoading] = useState(false);
  const patientsLoadedRef = useRef(false);

  // Track if any filter dropdown is open (to allow requests while selecting)
  const isFilterDropdownOpenRef = useRef(false);

  // Debounce providers filter - only update if actually changed
  const prevProvidersRef = useRef<number[]>(selectedProviders);
  useEffect(() => {
    // Skip if values are the same (prevents initial double-fetch)
    if (
      JSON.stringify(prevProvidersRef.current) ===
      JSON.stringify(selectedProviders)
    ) {
      return;
    }
    prevProvidersRef.current = selectedProviders;

    const timer = setTimeout(() => {
      setDebouncedProviders(selectedProviders);
    }, 1000);
    return () => clearTimeout(timer);
  }, [selectedProviders]);

  // Debounce patients filter - only update if actually changed
  const prevPatientsRef = useRef<number[]>(selectedPatients);
  useEffect(() => {
    // Skip if values are the same (prevents initial double-fetch)
    if (
      JSON.stringify(prevPatientsRef.current) ===
      JSON.stringify(selectedPatients)
    ) {
      return;
    }
    prevPatientsRef.current = selectedPatients;

    const timer = setTimeout(() => {
      setDebouncedPatients(selectedPatients);
    }, 1000);
    return () => clearTimeout(timer);
  }, [selectedPatients]);

  // Track pending requests: loading stays true until ALL in-flight requests complete
  const pendingRequestsRef = useRef(0);
  // Sequence number: only apply results from the latest request (discard stale responses)
  const latestRequestIdRef = useRef(0);
  const hasMountedRef = useRef(false);

  const getSessionType = (type: number) => {
    return type === 1 ? "Consultancy" : "Treatment";
  };

  // Fetch providers on dropdown open (lazy loading)
  const handleProvidersFilterOpen = useCallback(async () => {
    isFilterDropdownOpenRef.current = true;

    // Only fetch providers list once
    if (providersLoadedRef.current) return;

    try {
      setProvidersLoading(true);
      const providersData = await usersService.getProviders();
      setProviders(providersData);
      providersLoadedRef.current = true;
    } catch (err) {
      console.error("Error fetching providers:", err);
    } finally {
      setProvidersLoading(false);
    }
  }, []);

  // Handle providers filter close
  const handleProvidersFilterClose = useCallback(() => {
    isFilterDropdownOpenRef.current = false;
  }, []);

  // Fetch patients list (shared logic)
  const fetchPatients = useCallback(async () => {
    if (patientsLoadedRef.current) return;
    try {
      setPatientsLoading(true);
      const response = await patientsService.getPatients({
        limit: 100,
      });
      setPatients(response.data);
      patientsLoadedRef.current = true;
    } catch (err) {
      console.error("Error fetching patients:", err);
    } finally {
      setPatientsLoading(false);
    }
  }, []);

  // If page is opened with a preselected patient, eagerly load patients list
  // so the filter dropdown shows the correct patient label
  useEffect(() => {
    if (initialPatientId) {
      fetchPatients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch patients on dropdown open (lazy loading)
  const handlePatientsFilterOpen = useCallback(async () => {
    isFilterDropdownOpenRef.current = true;
    await fetchPatients();
  }, [fetchPatients]);

  // Handle patients filter close
  const handlePatientsFilterClose = useCallback(() => {
    isFilterDropdownOpenRef.current = false;
  }, []);

  // Fetch sessions from API
  const fetchSessions = useCallback(async () => {
    // Assign a unique ID to this request to detect stale responses
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;

    pendingRequestsRef.current += 1;
    setLoading(true);
    setError(null);

    try {

      // Map UI sort field to API field
      const getOrderByField = (field: string) => {
        switch (field) {
          case "date":
            return "startedAt";
          case "patient":
            return "PatientId";
          case "provider":
            return "providerName";
          default:
            return "startedAt";
        }
      };

      const response = await sessionsService.getSessions({
        search: debouncedSearch || undefined,
        patientId:
          debouncedPatients.length > 0 ? debouncedPatients : undefined,
        providerId:
          debouncedProviders.length > 0 ? debouncedProviders : undefined,
        start: (currentPage - 1) * sessionsPerPage,
        limit: sessionsPerPage,
        total: true,
        orderBy: getOrderByField(sortField),
        order: sortDirection === "asc" ? 0 : 1,
      });

      // Transform API sessions to display format
      const displaySessions: SessionDisplay[] = response.data.map(
        (session: Session) => {
          // Form provider full name from firstName and lastName
          const providerName = [
            session.providerFirstName,
            session.providerLastName,
          ]
            .filter(Boolean)
            .join(" ");

          // Form patient full name from firstName and lastName
          const patientName = [
            session.patientFirstName,
            session.patientLastName,
          ]
            .filter(Boolean)
            .join(" ");

          return {
            id: session.id,
            provider: {
              id: session.accountId,
              name: providerName || undefined,
              avatarUrl: session.providerAvatarUrl,
            },
            patient: patientName || `Patient ${session.patientUid}`,
            patientId: session.patientId,
            patientUid: session.patientUid,
            type: getSessionType(session.type),
            date: session.startedAt,
            treatmentPlan: session.treatmentPlan,
          };
        },
      );

      // Only apply results if this is still the latest request
      if (requestId === latestRequestIdRef.current) {
        setSessions(displaySessions);
        setTotalCount(response.total || 0);
      }
    } catch (err) {
      if (requestId === latestRequestIdRef.current) {
        setError("Failed to load sessions");
        setSessions([]);
        setTotalCount(0);
      }
      console.error("Error fetching sessions:", err);
    } finally {
      pendingRequestsRef.current -= 1;
      // Only stop loading when every in-flight request has completed
      if (pendingRequestsRef.current === 0) {
        setLoading(false);
      }
    }
  }, [
    debouncedSearch,
    currentPage,
    sessionsPerPage,
    sortField,
    sortDirection,
    debouncedProviders,
    debouncedPatients,
  ]);

  // Fetch sessions on mount and when dependencies change
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      fetchSessions();
      return;
    }
    fetchSessions();
  }, [fetchSessions]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.target.value);
    },
    [],
  );

  // Reset to first page when debounced filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, debouncedProviders, debouncedPatients]);

  const handleProvidersChange = useCallback((providerIds: number[]) => {
    setSelectedProviders(providerIds);
  }, []);

  const handlePatientsChange = useCallback((patientIds: number[]) => {
    setSelectedPatients(patientIds);
  }, []);

  const handleSort = useCallback((field: string, direction: "asc" | "desc") => {
    setSortField(field as SortField);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page when sorting changes
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleSessionClick = useCallback(
    (sessionId: number) => {
      navigate(`/sessions/${sessionId}`);
    },
    [navigate],
  );

  const handlePatientClick = useCallback(
    (patientId: number) => {
      navigate(`/patients/${patientId}`);
    },
    [navigate],
  );

  const handleTreatmentPlanClick = useCallback(
    (planId: number) => {
      navigate(`/treatment-plan/${planId}`);
    },
    [navigate],
  );

  const handleProviderClick = useCallback(
    (providerId: number) => {
      navigate(`/user-management/${providerId}`);
    },
    [navigate],
  );

  return (
    <Layout>
      <AllSessionsPageStyled>
        <SessionsHeader
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          providers={providers}
          selectedProviders={selectedProviders}
          onProvidersChange={handleProvidersChange}
          onProvidersFilterOpen={handleProvidersFilterOpen}
          onProvidersFilterClose={handleProvidersFilterClose}
          providersLoading={providersLoading}
          patients={patients}
          selectedPatients={selectedPatients}
          onPatientsChange={handlePatientsChange}
          onPatientsFilterOpen={handlePatientsFilterOpen}
          onPatientsFilterClose={handlePatientsFilterClose}
          patientsLoading={patientsLoading}
        />

        <SessionsTable
          sessions={sessions}
          sortField={sortField}
          sortDirection={sortDirection}
          currentPage={currentPage}
          pageSize={sessionsPerPage}
          totalCount={totalCount}
          loading={loading}
          error={error}
          showProviderColumn={isManagerOrAssistantRole}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onSessionClick={handleSessionClick}
          onPatientClick={handlePatientClick}
          onProviderClick={handleProviderClick}
          onTreatmentPlanClick={handleTreatmentPlanClick}
          onRetry={fetchSessions}
        />
      </AllSessionsPageStyled>
    </Layout>
  );
};

export default AllSessionsPage;
